import { Fragment, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';

import Button from '@/shared/components/Button';
import { PAGE_SIZE } from './useUsageData';
import { formatCost, formatCount } from './UsageSummary';

const COLUMNS = [
  { key: 'createdOn', label: 'Time', sortable: true },
  { key: 'firmName', label: 'Firm' },
  { key: 'userName', label: 'User' },
  { key: 'feature', label: 'Feature', sortable: true },
  { key: 'screenName', label: 'Screen' },
  { key: 'aiModel', label: 'Model', sortable: true },
  { key: 'inputTokens', label: 'In', sortable: true, align: 'right' },
  { key: 'outputTokens', label: 'Out', sortable: true, align: 'right' },
  { key: 'cost', label: 'Cost', sortable: true, align: 'right' },
];

const DETAIL_FIELDS = [
  ['metricType', 'Type'], ['billableServiceName', 'Service'], ['entityName', 'Entity'], ['recordId', 'Record'],
  ['reservationId', 'Reservation'], ['wordCount', 'Words'], ['resolution', 'Resolution'], ['quality', 'Quality'],
  ['searchQuery', 'Search query'], ['resultCount', 'Results'], ['urlCount', 'URLs'], ['contentSizeBytes', 'Bytes'],
  ['domainsScraped', 'Domains'],
];

const formatTime = (value) => (value ? new Date(value).toLocaleString() : '—');

const cellValue = (row, key) => {
  if (key === 'createdOn') return formatTime(row[key]);
  if (key === 'cost') return formatCost(row[key]);
  if (key === 'inputTokens' || key === 'outputTokens') return formatCount(row[key]);
  return row[key] ?? '—';
};

const RowDetail = ({ row }) => {
  const fields = DETAIL_FIELDS.filter(([key]) => row[key] !== null && row[key] !== undefined && row[key] !== '');
  return (
    <div className="space-y-3 px-4 py-3 text-sm">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-4">
        {fields.map(([key, label]) => (
          <div key={key}>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className="break-words text-foreground">{String(row[key])}</dd>
          </div>
        ))}
      </dl>
      {row.aiPrompt ? (
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">Prompt</p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-lg border border-solid border-border bg-muted p-3 text-xs text-foreground">
            {row.aiPrompt}
          </pre>
        </div>
      ) : null}
    </div>
  );
};

const SortIndicator = ({ active, direction }) => {
  if (!active) return null;
  return direction === 'asc' ? <ChevronUp className="ml-1 inline size-3" /> : <ChevronDown className="ml-1 inline size-3" />;
};

const UsageTable = ({ rows, isLoading, sort, onSort, page, totalPages, totalCount, onPageChange }) => {
  const [expandedId, setExpandedId] = useState(null);
  const firstShown = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastShown = Math.min(page * PAGE_SIZE, totalCount);

  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-solid border-border bg-card shadow-sm">
      <div className="overflow-x-auto">
      <table className="w-full whitespace-nowrap text-sm">
        <thead>
          <tr className="border-b border-solid border-border bg-muted text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                className={`px-4 py-3 ${column.align === 'right' ? 'text-right' : ''} ${column.sortable ? 'cursor-pointer select-none' : ''}`}
                onClick={column.sortable ? () => onSort(column.key) : undefined}
              >
                {column.label}
                <SortIndicator active={sort.orderBy === column.key} direction={sort.sortDirection} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && rows.length === 0 ? (
            <tr><td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-muted-foreground">Loading usage...</td></tr>
          ) : null}
          {!isLoading && rows.length === 0 ? (
            <tr><td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-muted-foreground">No usage matches these filters.</td></tr>
          ) : null}
          {rows.map((row) => (
            <Fragment key={row.id}>
              <tr
                onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                className="cursor-pointer border-b border-solid border-border text-foreground transition-colors hover:bg-muted"
              >
                {COLUMNS.map((column) => (
                  <td key={column.key} className={`px-4 py-2.5 ${column.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                    {cellValue(row, column.key)}
                  </td>
                ))}
              </tr>
              {expandedId === row.id ? (
                <tr className="border-b border-solid border-border bg-muted/40">
                  <td colSpan={COLUMNS.length} className="whitespace-normal"><RowDetail row={row} /></td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
      </div>

      <div className="flex items-center justify-between px-4 py-3 text-xs text-muted-foreground">
        <span>Showing {formatCount(firstShown)}–{formatCount(lastShown)} of {formatCount(totalCount)}</span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} leftIcon={<ChevronLeft className="size-4" />}>
            Previous
          </Button>
          <span>Page {page} of {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            Next <ChevronRight className="ml-1 size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UsageTable;
